import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import translations from "@/i18n/translations.json";

const LanguageContext = createContext(null);

export const LANGS = Object.entries(translations).map(([code, v]) => ({
  code, name: v._name || code, dir: v._dir || "ltr",
}));

// Detect initial language on first mount. Priority:
//   1. ?lang=xx query param (SEO alternate landings + shareable links)
//   2. Value stored in localStorage from a previous visit
//   3. Browser's navigator.language when it matches one we support
//   4. Default: English (global web-development business, US English default)
function detectInitialLang() {
  try {
    const url = new URL(window.location.href);
    const q = (url.searchParams.get("lang") || "").toLowerCase();
    if (q && translations[q]) return q;
  } catch (_e) { /* ignore */ }
  const stored = localStorage.getItem("jm_lang");
  if (stored && translations[stored]) return stored;
  const nav = (navigator.language || "").slice(0, 2).toLowerCase();
  if (nav && translations[nav]) return nav;
  return "en";
}

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(detectInitialLang);

  useEffect(() => {
    localStorage.setItem("jm_lang", lang);
    const dir = (translations[lang] && translations[lang]._dir) || "ltr";
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.setAttribute("dir", dir);
  }, [lang]);

  // Keep the ?lang query param in sync so alternate URLs are shareable and
  // Google can index each language variant separately via the hreflang tags.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const current = url.searchParams.get("lang");
      if (lang === "en") {
        if (current) {
          url.searchParams.delete("lang");
          window.history.replaceState({}, "", url.toString());
        }
      } else if (current !== lang) {
        url.searchParams.set("lang", lang);
        window.history.replaceState({}, "", url.toString());
      }
    } catch (_e) { /* ignore */ }
  }, [lang]);

  const t = useMemo(() => {
    const dict = translations[lang] || {};
    const en = translations.en || {};
    return (key) => dict[key] ?? en[key] ?? key;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, langs: LANGS }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
